import React, { useMemo, useRef, useState } from 'react';
import { Toast } from 'primereact/toast';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { FileUpload } from 'primereact/fileupload';
import { InputText } from 'primereact/inputtext';
import { Dialog } from 'primereact/dialog';
import { Tag } from 'primereact/tag';
import { useSelector } from 'react-redux';

import { HeaderProgram, PortalHeader } from './utils/utils';
import { importReceiptEnvanter, sendReceiptEnvanter as sendZenomReceiptEnvanter } from '../module/zenom/utils/ZenomRequest';
import Loading from '../Loading';

const extractInventoryResponse = (response) => {
    const candidates = [response?.data?.data?.data, response?.data?.data, response?.data];

    for (const candidate of candidates) {
        if (Array.isArray(candidate?.rows)) {
            return {
                rows: candidate.rows,
                rowCount: candidate.rowCount ?? candidate.rows.length
            };
        }

        if (Array.isArray(candidate)) {
            return {
                rows: candidate,
                rowCount: candidate.length
            };
        }
    }

    return {
        rows: [],
        rowCount: 0
    };
};

const extractResponseMessage = (response) => response?.data?.data?.message || response?.data?.message || '';

const formatAmount = (value) => {
    if (value === null || value === undefined || value === '') return '-';

    const stringValue = String(value);
    const normalizedAmount = stringValue.includes(',') ? stringValue.replace(/\./g, '').replace(',', '.') : stringValue;
    const amount = Number(normalizedAmount);

    if (Number.isNaN(amount)) return value;

    return new Intl.NumberFormat('tr-TR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    }).format(amount);
};

const withFallback = (value) => {
    if (value === null || value === undefined || value === '') return '-';
    return value;
};

const formatInventoryDate = (value) => {
    if (value === null || value === undefined || value === '') return '-';

    const dateText = String(value).trim();
    const dateParts = dateText.match(/^(\d{4})[./-](\d{1,2})[./-](\d{1,2})$/);

    if (dateParts) {
        const [, year, month, day] = dateParts;
        return `${day.padStart(2, '0')}.${month.padStart(2, '0')}.${year}`;
    }

    return dateText;
};

const inventoryUnitLabels = {
    TNE: 'ton',
    BX: 'Kutu',
    LTR: 'lt',
    C62: 'Adet',
    KGM: 'kg',
    KJO: 'kJ',
    GRM: 'g',
    MGM: 'mg',
    NT: 'Net Ton',
    GT: 'Gross Ton',
    MTR: 'm',
    MMT: 'mm',
    KTM: 'km',
    MLT: 'ml',
    MMQ: 'mm3',
    CLT: 'cl',
    CMK: 'cm2',
    CMQ: 'cm3',
    CMT: 'cm',
    DMK: 'dm2',
    MTK: 'm2',
    MTQ: 'm3',
    DAY: 'Gün',
    MON: 'Ay',
    PA: 'Paket',
    KWH: 'KWH',
    MWH: 'MWH',
    ANN: 'Yıl',
    HUR: 'Saat',
    D61: 'Dakika',
    D62: 'Saniye',
    CCT: 'Ton baş.taşıma kap.',
    D30: 'Brüt kalori',
    D40: '1000 lt',
    LPA: 'saf alkol lt',
    B32: 'kg.m2',
    NCL: 'hücre adet',
    PR: 'Çift',
    R9: '1000 m3',
    SET: 'Set',
    T3: '1000 adet',
    Q37: 'SCM',
    Q39: 'NCM',
    J39: 'mmBTU',
    G52: 'CM3',
    DZN: 'Düzine',
    CT: 'Karat'
};

const formatInventoryUnit = (unitCode) => inventoryUnitLabels[String(unitCode || '').trim()] || unitCode || '';

export default function ZenomInventoryPage() {
    const toast = useRef(null);
    const { selectedCompany } = useSelector((state) => state.userCompany);

    const [inventoryFile, setInventoryFile] = useState(null);
    const [inventoryRows, setInventoryRows] = useState([]);
    const [rowCount, setRowCount] = useState(0);
    const [globalFilter, setGlobalFilter] = useState(null);
    const [loading, setLoading] = useState(false);
    const [sendLoading, setSendLoading] = useState(false);
    const [sendDialogVisible, setSendDialogVisible] = useState(false);
    const [enteredBy, setEnteredBy] = useState('');

    const selectedModule = useMemo(() => selectedCompany?.modules?.find((module) => module.title === 'E-Defter'), [selectedCompany]);
    const programReq = selectedModule?.setting?.settings?.program?.requirements?.[0] || {};
    const webService = selectedModule?.setting?.settings?.webService || {};
    const webServiceReq = webService?.requirements || {};

    const inventoryDetailRows = useMemo(
        () =>
            inventoryRows.map((item, index) => ({
                ...item,
                _rowKey: `${item.EntryNumber || 'entry'}-${item.LineNumber || 'line'}-${item.AccountSubID || item.AccountMainID || 'account'}-${index}`
            })),
        [inventoryRows]
    );

    const sendEnabled = inventoryRows.length > 0 && !!inventoryFile && !loading && !sendLoading;

    const handleFileSelect = (event) => {
        const file = event.files?.[0] || null;

        setInventoryFile(file);
        setInventoryRows([]);
        setRowCount(0);
        setGlobalFilter(null);
        setSendDialogVisible(false);
        setEnteredBy('');
    };

    const handleLoadInventory = async () => {
        if (!inventoryFile) {
            toast.current.show({ severity: 'warn', summary: 'Uyarı', detail: 'Lütfen envanter HTML dosyası seçiniz.', life: 3000 });
            return;
        }

        const formData = new FormData();
        formData.append('html_file', inventoryFile);

        setLoading(true);

        try {
            const response = await importReceiptEnvanter(formData);
            const data = extractInventoryResponse(response);

            setInventoryRows(data.rows);
            setRowCount(data.rowCount);
            toast.current.show({
                severity: data.rows.length > 0 ? 'success' : 'info',
                summary: data.rows.length > 0 ? 'Başarılı' : 'Bilgi',
                detail: extractResponseMessage(response) || (data.rows.length > 0 ? `${data.rowCount} adet envanter kaydı getirildi.` : 'HTML içinde envanter kaydı bulunamadı.'),
                life: 3000
            });
        } catch (error) {
            toast.current.show({ severity: 'error', summary: 'Hata', detail: error?.response?.data?.message || error?.message || 'Envanter listesi getirilemedi.', life: 3000 });
        } finally {
            setLoading(false);
        }
    };

    const appendSendField = (formData, key, value) => {
        formData.append(key, value ?? '');
    };

    const handleSubmitSend = async (directSendValue) => {
        if (!enteredBy.trim()) {
            toast.current.show({ severity: 'warn', summary: 'Uyarı', detail: 'Lütfen gönderen kişi bilgisini giriniz.', life: 3000 });
            return;
        }

        if (!inventoryFile || inventoryRows.length === 0) {
            toast.current.show({ severity: 'warn', summary: 'Uyarı', detail: 'Gönderim için envanter HTML dosyasını yükleyiniz.', life: 3000 });
            return;
        }

        if (!programReq?.taxNumber) {
            toast.current.show({ severity: 'warn', summary: 'Uyarı', detail: 'Vergi numarası bilgisi bulunamadı.', life: 3000 });
            return;
        }

        if (!webService?.url || !webServiceReq?.username || !webServiceReq?.password) {
            toast.current.show({ severity: 'warn', summary: 'Uyarı', detail: 'Web servis bilgileri bulunamadı.', life: 3000 });
            return;
        }

        const formData = new FormData();
        appendSendField(formData, 'taxNumber', programReq.taxNumber);
        appendSendField(formData, 'enteredBy', enteredBy.trim());
        appendSendField(formData, 'eDefterSubeKodu', programReq.eDefterSubeKodu);
        appendSendField(formData, 'eDefterSubeAdi', programReq.eDefterSubeAdi);
        appendSendField(formData, 'webServiceUrl', webService.url);
        appendSendField(formData, 'username', webServiceReq.username);
        appendSendField(formData, 'password', webServiceReq.password);
        appendSendField(formData, 'direct_send', directSendValue);
        formData.append('html_file', inventoryFile);

        setSendLoading(true);

        try {
            const response = await sendZenomReceiptEnvanter(formData);
            toast.current.show({ severity: 'success', summary: 'Başarılı', detail: extractResponseMessage(response) || 'Envanter gönderildi.', life: 3000 });
            setSendDialogVisible(false);
            setEnteredBy('');
        } catch (error) {
            error.code === 'ERR_NETWORK'
                ? toast.current.show({ severity: 'error', summary: 'Hata', detail: 'İstek zaman aşımına uğradı', life: 3000 })
                : toast.current.show({ severity: 'error', summary: 'Hata', detail: error?.response?.data?.message || error?.message || 'Envanter gönderilemedi.', life: 3000 });
        } finally {
            setSendLoading(false);
        }
    };

    const accountMainTemplate = (rowData) => (
        <div className="flex flex-column gap-1">
            <span className="font-bold">{withFallback(rowData.AccountMainID)}</span>
            <span>{withFallback(rowData.AccountMainDescription)}</span>
        </div>
    );

    const accountSubTemplate = (rowData) => (
        <div className="flex flex-column gap-1">
            <span className="font-bold">{withFallback(rowData.AccountSubID)}</span>
            <span>{withFallback(rowData.AccountSubDescription)}</span>
        </div>
    );

    const entryTemplate = (rowData) => (
        <div className="flex flex-column gap-1">
            <span className="font-bold">{withFallback(rowData.EntryComment)}</span>
        </div>
    );

    const measurableTemplate = (rowData) => (
        <span>{[withFallback(rowData.measurableQuantity), formatInventoryUnit(rowData.measurableUnitOfMeasure)].filter((item) => item && item !== '-').join(' ') || '-'}</span>
    );

    const detailHeader = (
        <div className="flex flex-wrap gap-2 align-items-center justify-content-between">
            <div className="flex flex-column gap-1">
                <h4 className="m-0">Envanter Kayıtları</h4>
                {inventoryRows.length > 0 && (
                    <div className="flex align-items-center gap-2">
                        <Tag className="text-white" severity="info" value={`${rowCount} kayıt`} />
                        {inventoryFile?.name && <span className="text-sm text-color-secondary">{inventoryFile.name}</span>}
                    </div>
                )}
            </div>
            <span className="p-input-icon-left">
                <i className="pi pi-search" />
                <InputText type="search" onInput={(event) => setGlobalFilter(event.target.value)} placeholder="Ara..." />
            </span>
        </div>
    );

    if (loading) {
        return (
            <div>
                <Toast ref={toast} />
                <div className="w-full" style={{ height: 'calc(100vh - 8rem)' }}>
                    <Loading loading={loading} onChangeLoading={() => setLoading(false)} />
                </div>
            </div>
        );
    }

    return (
        <div>
            <Toast ref={toast} />
            <div className="flex align-items-center justify-content-between">
                <HeaderProgram selectedCompany={selectedCompany} moduleName="E-Defter" />
            </div>
            <br />
            <h3 className="mb-5">Zenom Envanter</h3>

            <div className="card flex align-items-end justify-content-between flex-wrap gap-4">
                <div className="flex align-items-end gap-5 flex-wrap">
                    <div>
                        <label className="mb-2 block">Envanter Dosyası</label>
                        <FileUpload chooseLabel="Envanter HTML dosyasını seçiniz." mode="basic" accept=".html,.htm" maxFileSize={100000000} onSelect={handleFileSelect} />
                        <small className="block text-pink-500 absolute">*Dosya uzantısı .html olmalıdır</small>
                    </div>
                    <Button icon="pi pi-ticket" onClick={handleLoadInventory} className="px-4" label="Dosyayı Yükle" disabled={!inventoryFile || loading} loading={loading} />
                    <Button icon="pi pi-send" label="Gönder" severity="success" className="text-white" disabled={!sendEnabled} onClick={() => setSendDialogVisible(true)} loading={sendLoading} />
                </div>
                <div className="flex align-items-end gap-5">
                    <PortalHeader selectedCompany={selectedCompany} moduleName="E-Defter" />
                </div>
            </div>

            <div className="card">
                <DataTable
                    value={inventoryDetailRows}
                    dataKey="_rowKey"
                    paginator
                    rows={10}
                    rowsPerPageOptions={[5, 10, 25, 50, 100]}
                    paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                    currentPageReportTemplate="{totalRecords} kayıttan {first} ila {last} arası gösteriliyor"
                    globalFilter={globalFilter}
                    header={detailHeader}
                    emptyMessage="Envanter kaydı bulunamadı"
                    className="p-datatable-gridlines"
                >
                    <Column field="AssetsDate" header="Tarih" sortable body={(rowData) => formatInventoryDate(rowData.AssetsDate)} style={{ minWidth: '9rem' }} />
                    <Column header="Fiş" body={entryTemplate} sortable sortField="EntryComment" style={{ minWidth: '16rem' }} />
                    <Column header="Ana Hesap" body={accountMainTemplate} sortable sortField="AccountMainID" style={{ minWidth: '16rem' }} />
                    <Column header="Alt Hesap" body={accountSubTemplate} sortable sortField="AccountSubID" style={{ minWidth: '16rem' }} />
                    <Column field="Amount" header="Tutar" sortable body={(rowData) => formatAmount(rowData.Amount)} style={{ minWidth: '10rem' }} />
                    <Column field="DetailComment" header="Detay Açıklama" sortable style={{ minWidth: '14rem' }} />
                    <Column header="Birim" body={measurableTemplate} sortable sortField="measurableUnitOfMeasure" style={{ minWidth: '10rem' }} />
                </DataTable>
            </div>

            <Dialog
                visible={sendDialogVisible}
                onHide={!sendLoading ? () => setSendDialogVisible(false) : () => {}}
                header="Envanter Gönder"
                modal
                style={{ width: '660px' }}
                footer={
                    <div className="flex justify-content-end flex-wrap gap-2">
                        <Button
                            icon="pi pi-check"
                            label={sendLoading ? 'Envanter Gönderiliyor...' : 'Envanter Defterini Taslaklara Kaydet'}
                            className="text-white"
                            onClick={() => handleSubmitSend(0)}
                            disabled={!enteredBy.trim() || sendLoading}
                            loading={sendLoading}
                        />
                        <Button
                            icon="pi pi-check"
                            label={sendLoading ? 'Envanter Gönderiliyor...' : 'Envanter Defterini Oluştur ve Portale Gönder'}
                            className="text-white"
                            onClick={() => handleSubmitSend(1)}
                            disabled={!enteredBy.trim() || sendLoading}
                            loading={sendLoading}
                        />
                    </div>
                }
            >
                <div className="flex flex-column gap-4 pt-2">
                    <div className="surface-100 border-round p-3 text-sm">
                        <div className="font-semibold">{inventoryFile?.name}</div>
                        <div className="text-color-secondary mt-1">{inventoryRows.length} kayıt gönderime hazırlanıyor.</div>
                    </div>
                    <div className="flex flex-column gap-2">
                        <label className="font-medium">
                            Gönderen Kişi <span className="text-pink-500 font-bold">*</span>
                        </label>
                        <InputText value={enteredBy} onChange={(event) => setEnteredBy(event.target.value)} placeholder="Gönderen kişi giriniz" autoFocus />
                    </div>
                </div>
            </Dialog>
        </div>
    );
}
